/**
 * React Query Mutations for Agent Configuration
 *
 * Mutation hooks for creating, updating, and deleting agents
 */

import { useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import { agentApi } from '../api';
import { QUERY_KEYS } from './useAgentsQuery';
import type { AgentConfiguration, AgentConfigResponse } from '@/types';

/**
 * Create new agent configuration
 */
export function useCreateAgentMutation(): UseMutationResult<
  AgentConfigResponse,
  Error,
  { config: AgentConfiguration }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ config }) => agentApi.create(config),
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentsList() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentConfig(data.agent_type) });
    },
  });
}

/**
 * Update agent configuration
 */
export function useUpdateAgentMutation(): UseMutationResult<
  AgentConfigResponse,
  Error,
  { agentType: string; config: Partial<AgentConfiguration>; description?: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentType, config, description }) =>
      agentApi.update(agentType, config, description),
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentConfig(data.agent_type) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentsList() });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentVersions(data.agent_type) });
    },
  });
}

/**
 * Delete agent configuration
 */
export function useDeleteAgentMutation(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentType: string) => agentApi.delete(agentType),
    onSuccess: (_, agentType) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentsList() });
      queryClient.removeQueries({ queryKey: QUERY_KEYS.agentConfig(agentType) });
    },
  });
}

/**
 * Activate specific version
 */
export function useActivateVersionMutation(): UseMutationResult<
  AgentConfigResponse,
  Error,
  { agentType: string; version: number; reason?: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentType, version, reason }) => agentApi.activateVersion(agentType, version, reason),
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentConfig(data.agent_type) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agentVersions(data.agent_type) });
    },
  });
}

/**
 * Invalidate agent cache
 */
export function useInvalidateCacheMutation(): UseMutationResult<void, Error, string | undefined> {
  return useMutation({
    mutationFn: (agentType?: string) => agentApi.invalidateCache(agentType),
  });
}

/**
 * Export agent configurations
 */
export function useExportConfigsMutation(): UseMutationResult<Blob, Error, string[] | undefined> {
  return useMutation({
    mutationFn: (agentTypes?: string[]) => agentApi.export(agentTypes),
  });
}

/**
 * Import agent configurations
 */
export function useImportConfigsMutation(): UseMutationResult<
  any,
  Error,
  { file: File; mode?: 'create-only' | 'overwrite' | 'merge'; skipErrors?: boolean }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, mode, skipErrors }) => agentApi.import(file, mode, skipErrors),
    onSuccess: () => {
      // Invalidate all agent queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.agents });
    },
  });
}
